import { Alert, Checkbox, FormControlLabel, Stack } from "@mui/material";
import React, { useState, useEffect } from "react";
import { ItemList, UserData, WitnessList } from "../../../types/user-data";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ReactComponent as AlertIcon } from "../../../Assets/Alert.svg";
import { ReactComponent as UploadIcon } from "../../../Assets/Upload.svg";

import { ReactComponent as CloseFileIcon } from "../../../Assets/CloseFile.svg";
import { ReactComponent as SuccessIcon } from "../../../Assets/Success.svg";
import { ReactComponent as CheckboxIcon } from "../../../Assets/Checkbox.svg";
import { v4 as uuid } from "uuid";
import classNames from "classnames";
import useCheckScreenSize from "../../../utils/checkScreenSize";
import "./upload.css";
import { DeleteFile } from "./DeleteFile";
import CircularProgress from "@mui/material/CircularProgress";
import { InfoBanner } from "../../../utils/InfoBanner";
import { INITIAL_DATA } from "../../../configs/initial-data";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import { Box } from "@mui/material";

interface Props extends UserData {
  setClaimId: (value: string) => void;
  setMultipleImages: (value: any) => void;
  multipleImages: any;
  data: Partial<UserData>;
  ItemListData: ItemList[];
  witnessListData: WitnessList[];
  updateFields: (fields: Partial<UserData>) => void;
  setWitnessListData: (fields: any) => void;
  setItemList: (fields: any) => void;
}

const AcceptedFileType = [
  "txt",
  "jpg",
  "png",
  "doc",
  "pdf",
  "docx",
  "xls",
  "xlsx",
  "heic",
  "jpeg"
];

export const SupportingDocuments: React.FC<Props> = ({
  data,
  ItemListData,
  witnessListData,
  setClaimId,
  setMultipleImages,
  multipleImages,
  updateFields,
  setWitnessListData,
  setItemList,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onTouched",
    reValidateMode: "onChange",
    shouldFocusError: false,
  });
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isMobile = useCheckScreenSize({ breakpoint: 850 });
  const [singleFile, setSingleFile] = useState<[]>([]);
  const [submitted, setSubmitted] = useState(false);

  const [fileSizeExceed, setFileSizeExceed] = useState(false);
  const [fileAlreadyExist, setFileAlreadyExist] = useState(false);
  const [fileFormatSupport, setFileFormatSupport] = useState(false);
  const [agree, setAgree] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState("");
  const [systemError, setSystemError] = useState(false);
  const [processingError, setProcessingError] = useState(false);
  const UUID = uuid().toString().replaceAll("-", "");
  const [filename, setFilename] = useState("");
  const [openAgreement, setOpenAgreement] = React.useState(false);
  const [openSubmit, setOpenSubmit] = React.useState(false);
  const recaptchaRef = React.createRef() as any;
  const recaptchaLanguage = i18n.resolvedLanguage === "zh"
      ? "zh-TW": i18n.resolvedLanguage === "es" ? "es": "en";
  useEffect(() => {
    window.scrollTo(0, 0); 
  }, []);

  function onCaptchaChange(value: any) {
    setToken(value);
  }
  const handleAgree = (e: any) => {
    setAgree(e.target.checked);
    if (e.target.checked) {
      data.agreement_signed = "Yes";
      if ((document.getElementById("fileupload") as HTMLInputElement).value !== ""){        
            handleClickOpenAgreement();       
      }
    } else {
      data.agreement_signed = "No";
    }
  };

  const handleBack = () => {
    data.agreement_signed = "No";
    navigate("../Witnesses");
  };
  const handleFileDelete = (index: any) => {
    const tempFiles = [...multipleImages];
    tempFiles.splice(index, 1);
    setMultipleImages(tempFiles);
  };
  const uploadMultipleFiles = (singleFile: any) => {
    const fileExtension = singleFile?.name?.split(".").at(-1);
    setFilename("");
    const findDuplicate = multipleImages.map((image: any, index: number) => {
      if (image.name === singleFile.name) {
        setFileSizeExceed(false);
        setFileAlreadyExist(true);
        setFileFormatSupport(false);

        setSingleFile([]);
        (document.getElementById("fileupload") as HTMLInputElement).value = "";
        return true;
      }
      return false;
    });

    if (!findDuplicate.includes(true)) {
      if (!AcceptedFileType.includes(fileExtension.toLowerCase())) {
        setFileSizeExceed(false);
        setFileAlreadyExist(false);
        setFileFormatSupport(true);

        setSingleFile([]);
        (document.getElementById("fileupload") as HTMLInputElement).value = "";
        return false;
      } else if (singleFile.size > 10485760) {
        setFileSizeExceed(false);
        setFileAlreadyExist(false);

        setFileSizeExceed(true);
        setFileFormatSupport(false);
        setSingleFile([]);
        (document.getElementById("fileupload") as HTMLInputElement).value = "";
        return false;
      } else {
        if (singleFile) {
          setMultipleImages((prevImages: string | any[]) =>
            prevImages.concat(singleFile)
          );

          setFileSizeExceed(false);
          setFileAlreadyExist(false);
          setFileFormatSupport(false);
        }
        setSingleFile([]);
        (document.getElementById("fileupload") as HTMLInputElement).value = "";
      }
    }
  };

  async function formSubmission() {
    //deleting incident_location and claimant_owner_of_vehicle from the form data since these are not required
    delete data["incident_location"];
    delete data["claimant_owner_of_vehicle"];
    delete data["is_question1"];
    delete data["is_question2"];
    delete data["is_question3"];
    const requestBody = {
      request_id: UUID,
      language_code: i18n.resolvedLanguage,
      total_attachments: multipleImages.length,
      claim: data,
      item_list: ItemListData,
      witness_list: witnessListData,
    };
    try {
      const response = await fetch(
        process.env.REACT_APP_FORM_POST_URL as string,
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );
      if (response.status === 200) {
        updateFields(INITIAL_DATA);
        setWitnessListData([]);
        setItemList([]);
      }

      if (
        response.status === 500 ||
        response.status === 502 ||
        response.status === 403
      ) {
        setSystemError(true);
        setLoading(false);
      }
      if (response.status === 400) {
        setProcessingError(true);
        setLoading(false);
      }
      let res = await response.json();

      if (res.claim_id) {
        setClaimId(res.claim_id);
      }
      return res;
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  }

  const documentArray: any[] = [];

  async function UploadApiCall(formData: any, fileName: any) {
    documentArray.push(fileName);
    const response = await fetch(
      process.env.REACT_APP_UPLOAD_S3_URL as string,
      {
        method: "POST",
        body: formData,
      }
    );

    if (response.status !== 204) {
      let fileRetryCount = documentArray.filter((arr: any) => arr === fileName);
      setUploadError(true);

      if (fileRetryCount.length < 3) {
        UploadApiCall(formData, fileName);
      }
    }
  }

  const asyncGetCallUpload = async (response: any) => {
    multipleImages.forEach((image: any, index: number) => {
      const i = index + 1;
      const keyvalue = `uploads/${
        response.claim_id
      }_${UUID}_${i}_${image.name.toLowerCase()}`;

      let formData = new FormData();
      formData.append("acl", "private");
      formData.append("key", keyvalue);
      formData.append("x-amz-algorithm", response.fields["x-amz-algorithm"]);
      formData.append("x-amz-credential", response.fields["x-amz-credential"]);
      formData.append("x-amz-date", response.fields["x-amz-date"]);
      formData.append(
        "x-amz-security-token",
        response.fields["x-amz-security-token"]
      );
      formData.append("policy", response.fields.policy);
      formData.append("x-amz-signature", response.fields["x-amz-signature"]);
      formData.append("file", multipleImages[index]);
      if (response.fields) {
        UploadApiCall(formData, image.name);
      }
    });
  }; 
  const handleSubmitClaims = () => {
    setSubmitted(true);  
    
    async function callFormApi() {
      const formSubmissionResponse = await formSubmission();

      if (
        formSubmissionResponse &&
        formSubmissionResponse.statusCode === 200 &&
        !formSubmissionResponse.error
      ) {
        setLoading(false);
        asyncGetCallUpload(formSubmissionResponse);
        if (!uploadError) {
          navigate("../Confirmation");
        }
      }
    }

    if (
      !errors.length &&
      token.length > 0 &&
      token !== "null" &&
      data.agreement_signed === "Yes"
    ) {
      callFormApi();
      setLoading(true);
    }    
  };

  const handleClickOpenAgreement = () => {
    setOpenAgreement(true);
  };
 
  const handleCloseAgreement = () => {
    setOpenAgreement(false);
  };

  const handleForgotUpload = () => {  
    setOpenAgreement(false);
  };
  
  const handleSubmitClaimsDialog = () => {
    if(data.agreement_signed != "Yes"){
      document.getElementById('Agreement')?.focus();
      var element = document.getElementById("agree_error");
      if (element) {
      element.className += " agreeError";
      }          
      return false;
    }
    if (!token) {
      setToken("null");  
    }
    if (token=="null") {     
      if(data.agreement_signed == "Yes") {
        let x:any=recaptchaRef.current; 
        if(x){
          x.captcha.tabIndex=0;
          x.captcha.focus();
        }else{
          let y:any=document.getElementById('recaptchaRefDiv');
          y.tabindex=0;
          y.focus();
        }
      }
      return false;
    } 
    if(multipleImages.length==0 ){
      setOpenSubmit(true);
      return false;
    }  
    handleSubmitClaims();
  };

  const handleSubmitAnywayClaims = () => {    
    setOpenSubmit(false);
    handleSubmitClaims();
  };

  const handleDontSubmit = () => {    
    setOpenSubmit(false);
    return false;
  };
  
  return (
    <div>
      <Stack sx={{ width: "100%" }} spacing={2}>
        {uploadError && (
          <Alert
            icon={
              <div>
                <AlertIcon />
              </div>
            }
            sx={{
              background: "#F7D8D8",
              color: "black",
              boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.07)",
              fontWeight: "bold",
              marginTop: "10px",
              marginBottom: "15px",
            }}
          >
            Error: We have encountered a problem processing your upload
            document. Please re-upload the document.
          </Alert>
        )}
        {systemError && (
          <Alert
            icon={
              <div>
                <AlertIcon />
              </div>
            }
            sx={{
              background: "#F7D8D8",
              color: "black",
              boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.07)",
              fontWeight: "bold",
              marginTop: "10px",
              marginBottom: "15px",
            }}
          >
            {t('ErrorSubmitForm')}
          </Alert>
        )}
        {processingError && (
          <Alert
            icon={
              <div>
                <AlertIcon />
              </div>
            }
            sx={{
              background: "#F7D8D8",
              color: "black",
              boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.07)",
              fontWeight: "bold",
              marginTop: "10px",
              marginBottom: "15px",
            }}
          >
            {t('ErrorProcessingRequest')}
          </Alert>
        )}
        {fileFormatSupport ? (
          <Alert
            icon={
              <div>
                <AlertIcon />
              </div>
            }
            sx={{
              background: "#F7D8D8",
              color: "black",
              boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.07)",
              fontWeight: "bold",
              marginTop: "10px",
              marginBottom: "15px",
            }}
          >
            <div style={{ marginTop: "3px" }}>
              {t('ErrorAllowedFileTypes')}
            </div>
          </Alert>
        ) : null}
        {fileSizeExceed ? (
          <Alert
            icon={
              <div>
                <AlertIcon />
              </div>
            }
            sx={{
              background: "#F7D8D8",
              color: "black",
              boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.07)",
              fontWeight: "bold",
              marginTop: "10px",
              marginBottom: "15px",
            }}
          >
            <div style={{ marginTop: "3px" }}>
              {t('ErrorFileSizeLessThan10MB')}
            </div>
          </Alert>
        ) : null}
        {fileAlreadyExist ? (
          <Alert
            icon={
              <div>
                <AlertIcon />
              </div>
            }
            sx={{
              background: "#F7D8D8",
              color: "black",
              boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.07)",
              fontWeight: "bold",
              marginTop: "10px",
              marginBottom: "15px",
              alignContent: "center",
            }}
          >
            <div style={{ marginTop: "3px" }}>
              {t('ErrorFileAlreadyUploaded')}
            </div>
          </Alert>
        ) : null}
      </Stack>
      <h2 className="subheader">{t('SupportingDocumentsHeading')}</h2>
      <div style={{ marginBottom: "20px" }}>{t('RequiredField')} </div>
      <div className="description">
        {t('ProcessClaimsDescription')}
      </div>
      <div className="description">
        <ul>
          <li style={{ fontWeight: "700" }}>
            <strong>{t('AddUpTo10Documents')}</strong>
          </li>
        </ul>
      </div>
      <div className="description">
        <ul>
          <li style={{ fontWeight: "700" }}>
            {" "}
            <strong>{t('FilesLessThan10MB')}</strong>
          </li>
        </ul>
      </div>
      <div className="description">
        <ul>
          <li style={{ fontWeight: "700" }}>
            <strong>
              {t('FileTypesAccepted')}
            </strong>
          </li>
        </ul>
      </div>
      <div className="description">
        <span style={{ fontWeight: "700" }}>{t('NOTE')} </span> {t('NoteClaimsRepresentative')}
      </div>
      <div className="label" style={{ marginBottom: "5px" }}>
        <label htmlFor="attachfileLabel">{t('AttachFile')}</label>
      </div>
      <div className="description">
        <ol id="attachfileLabel">
          <li>
            {t('AttachFileInstruction1')}<UploadIcon />{t('AttachFileInstruction1End')}
          </li>

          <li>{t('AttachFileInstruction2')}</li>
          <li>{t('AttachFileInstruction3')}</li>
        </ol>
      </div>

      <br />
      <div >
        <div style={{ marginRight: "15px" }}>
          <span id="file-selected" className="fileName">
            {filename}
          </span>

          <div className="input">
            <input
              id="fileupload"
              type="file"
              className="upload"
              title={String(t('NoFileChosen'))}
              {...register("file", {
                onChange: (e: any) => {
                  setSingleFile(e.target?.files[0]);
                  setFilename(e.target?.files[0].name);
                },
              })}
              disabled={multipleImages.length === 10 ? true : false}
            />
            <label className="imageUploader" htmlFor="fileupload">
              <div
                style={{ marginLeft: "-30px" }}
                className="secondButton"
                aria-label="upload"
              >
                {filename &&
                multipleImages.length > 0 &&
                multipleImages?.find(
                  (image: any) => image.name !== filename
                ) ? (
                  <SuccessIcon />
                ) : (
                  <UploadIcon />
                )}
              </div>
              <button
                onClick={() => setFilename("")}
                className="closeFile secondButton"
                aria-label="clearFile"
                title={String(t('RemoveFile'))}
              >
                <CloseFileIcon />
              </button>
            </label>
          </div>
          <div style={{ marginTop: "10px" }}>{t('MaxFileSize')}</div>
        </div>     
        <br />
        
        <button
          className={classNames(
            multipleImages.length === 10 ? "disableButton" : "blueButton"
          )}
          onClick={() => {
            if (singleFile) {
              uploadMultipleFiles(singleFile);
            }
          }}
          disabled={multipleImages.length === 10 || singleFile.length === 0}
          title={String(t('Upload'))}
        >
          {t('Upload')}{" "}
          <UploadIcon title={String(t('arrowUP'))}/>
        </button>
      </div>
      <br />
      <br />
      {multipleImages.length > 0 && <div className="label">{t('FilesAttached')}</div>}
      <br />
      {multipleImages.length > 0
        ? multipleImages.map((image: any, index: number) => {
            return (
              <div
                className="row"
                key={image.name}
                style={{ marginBottom: "8px" }}
              >
                <label>{image.name}</label>
                <div className="deleteFile">
                  <DeleteFile
                    index={index}
                    handleDelete={handleFileDelete}
                    {...multipleImages}
                  />
                </div>
              </div>
            );
          })
        : null}
      <br />
      <form onSubmit={handleSubmit(handleSubmitClaims)}>
        <div className="label">
          <label htmlFor="Agreement">*{t('Agreement')}</label>
        </div>
        <div className="row" style={{ marginTop: "10px" }}>
          <div>
            <FormControlLabel
              id="Agreement"
              aria-labelledby="Agreement agree_error"
              aria-describedby="agree_error"
              control={
                <div style={{ alignSelf: "start", marginTop: "-6px" }}>
                  <Checkbox                    
                    sx={errors.check && { color: "#e2231a" }}
                    checkedIcon={<CheckboxIcon className="checkSize" />}
                  />
                </div>
              }
              checked={agree}
              {...register("check", {
                validate: (value) => value === true,
              })}
              onChange={(e) => handleAgree(e)}
              label={t('AgreementTerms')}
            />
            </div>
            <Dialog
            open={openAgreement}
            onClose={handleCloseAgreement}
            aria-labelledby="aggreement-alert-dialog-title"
            aria-describedby="aggreement-alert-dialog-description"
            >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                m: "auto",
                width: "fit-content",
              }}
              className={classNames(!isMobile && "popup")}
            >
              <DialogTitle id="aggreement-alert-dialog-title">
                <h2 className="subheader">{t('FileNotUploaded')}</h2>
              </DialogTitle>
              <DialogContent>
                <div id="aggreement-alert-dialog-description">{t('FileNotUploadedMessage')}</div>
                <br />
                <br />
                <div style={{ marginTop: "10px",marginBottom: "50px" }}>
                  <button
                    className={classNames(
                      "yellowButton",
                      isMobile && "popupInputMobileItem"
                    )}
                    onClick={handleForgotUpload}
                  >
                    {t('Ok')}
                  </button>
                </div> 
                <br />
              </DialogContent>
            </Box>
          </Dialog>     
        </div>

        <div className="errorText errorhidden" id="agree_error">
            {t('ErrorReadAgreeTerms')}
        </div>

        <br />
        <div className="formLabel">*{t('Security')}</div>
        <div className="inputDescription">
          {t('VerifyNotRobot')}
        </div>
        <label htmlFor="recaptcha" className="visuallyhidden">
          {t('Recaptcha')}
        </label>
        <div aria-live="polite" id='recaptchaRefDiv'>
          <ReCAPTCHA
            key={recaptchaLanguage}
            aria-required="true"
            id="recaptcha"
            aria-labelledby="recaptcha reCAPTCHA_error"
            aria-describedby="reCAPTCHA_error"
            hl={recaptchaLanguage}
            sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY as string}
            onChange={onCaptchaChange}
            theme="dark"
            ref={recaptchaRef}
          />
        </div>

        {token === "null" && (
          <div className="errorText" id="reCAPTCHA_error">
            {t('ErrorVerifyNotRobot')}
          </div>
        )}
        <br />
        <br />
        <button
          className={classNames({ onLoad: loading }, "yellowLoadingButton")}
          type="submit"
          onClick={handleSubmitClaimsDialog}
          disabled={loading}
        >
          {t('Submit')} {loading && <CircularProgress color="inherit" size={15} />}
        </button>
        <Dialog
          open={openSubmit}
          onClose={handleDontSubmit}
          aria-labelledby="submit-alert-dialog-title"
          aria-describedby="submit-alert-dialog-description"
          disableEnforceFocus
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              m: "auto",
              width: "fit-content",
            }}
            className={classNames(!isMobile && "popup")}
          >
            <DialogTitle id="submit-alert-dialog-title">
              <h2 className="subheader">{t('SubmitWithoutAttachment')}</h2>
            </DialogTitle>
            <DialogContent>
              <div id="submit-alert-dialog-description">{t('SubmitWithoutUploadingDocuments')}</div>
              <br />
              <br />
              <div style={{ marginTop: "10px" }}>
                <button
                  type="submit" 
                  className={classNames(
                    "yellowButton",
                    isMobile && "popupInputMobileItem"
                  )}
                  onClick={handleSubmitAnywayClaims}
                >
                  {t('SubmitAnyway')}
                </button>
              </div>

              <div className="secondButtonDiv">
                <button className="secondButton" onClick={handleDontSubmit}>
                {t('DontSubmit')}
                </button>
              </div>
              <br />
            </DialogContent>
          </Box>
        </Dialog>
        <div className="secondButtonDiv">
          <button onClick={handleBack} className="secondButton">
            {t('Back')}
          </button>
        </div>
      </form>
      <InfoBanner />
    </div>
  );
};